import React, { useState, useContext, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/solid";
import Navbar from "../components/Navbar";
import GlowButton from "../components/GlowButton";
import MarketingBackdrop from "../components/MarketingBackdrop";
import SEO from "../components/SEO";
import { loginUser } from "../api/auth";
import { AuthContext } from "../context/AuthContext";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("error") === "google_auth_failed") {
      setError("Google authentication failed. Please try again.");
    }
  }, [searchParams]);

  const handleNormalLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await loginUser({ username: email, password });
      login(res.data.user, {
        access: res.data.access,
        refresh: res.data.refresh,
      });
      navigate("/portfolio");
    } catch (error) {
      console.error(error.response?.data);
      setError(error.response?.data?.detail || "Login failed — check email/password");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUri = `${window.location.origin}/auth/google/callback`;
    const googleUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${process.env.REACT_APP_GOOGLE_CLIENT_ID}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=id_token&scope=email%20profile&nonce=${Date.now()}`;
    window.location.href = googleUrl;
  };

  return (
    <div className="relative isolate min-h-screen bg-white dark:bg-[#060B12] text-gray-900 dark:text-white">
      <SEO path="/login" title="Log in" description="Log in to FinAI." noindex />
      <MarketingBackdrop />
      <Navbar />

      <div className="relative min-h-screen flex items-center justify-center px-4 pt-32 pb-16">
        <div className="w-full max-w-md">
          {/* Live badge */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/60 dark:bg-white/5 border border-gray-200 dark:border-white/10 backdrop-blur-sm">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              <span className="text-xs font-semibold tracking-wide text-gray-700 dark:text-gray-200">
                Three AI agents waiting
              </span>
            </div>
          </div>

          <div className="rounded-3xl bg-white/70 dark:bg-[#0b1220]/70 backdrop-blur-xl border border-gray-200 dark:border-white/10 shadow-2xl p-8 sm:p-10">
            <h1 className="text-3xl sm:text-4xl font-extrabold text-center mb-2 tracking-tight">
              Welcome back to{" "}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-emerald-400 via-sky-400 to-purple-500">
                FinAI
              </span>
            </h1>
            <p className="text-center text-sm text-gray-500 dark:text-gray-400 mb-8">
              Sign in to brief with Sage, Pulse and Echo.
            </p>

            {error && (
              <div className="mb-5 p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-500 dark:text-red-300 text-sm">
                {error}
              </div>
            )}

            <button
              onClick={handleGoogleLogin}
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 bg-white text-gray-900 py-3 rounded-xl font-semibold border border-gray-200 hover:bg-gray-50 transition disabled:opacity-50 shadow-sm"
            >
              <img
                src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                className="w-5"
                alt="Google"
              />
              Continue with Google
            </button>

            <div className="flex items-center my-6">
              <div className="flex-1 h-px bg-gray-300 dark:bg-white/10" />
              <span className="px-3 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                or
              </span>
              <div className="flex-1 h-px bg-gray-300 dark:bg-white/10" />
            </div>

            <form onSubmit={handleNormalLogin} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                  Email or Username
                </label>
                <input
                  type="text"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-white dark:bg-white/5 text-gray-900 dark:text-white border border-gray-300 dark:border-white/10 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500/60 transition"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 pr-12 rounded-xl bg-white dark:bg-white/5 text-gray-900 dark:text-white border border-gray-300 dark:border-white/10 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500/60 transition"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-emerald-500 transition"
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="w-5 h-5" />
                    ) : (
                      <EyeIcon className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex justify-center pt-3">
                <GlowButton type="submit" size="md" onClick={undefined}>
                  {loading ? "SIGNING IN..." : "SIGN IN"}
                </GlowButton>
              </div>
            </form>

            <p className="text-center mt-6 text-sm text-gray-600 dark:text-gray-400">
              Don't have an account?{" "}
              <Link
                to="/signup"
                className="font-semibold bg-clip-text text-transparent bg-gradient-to-r from-emerald-400 to-purple-500 hover:opacity-80 transition"
              >
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
